import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  RiLockPasswordLine,
  RiShieldCheckLine,
  RiErrorWarningLine,
  RiCheckDoubleLine,
  RiArrowRightLine,
  RiMailLine,
  RiUser3Line } from
'react-icons/ri';
import api from '../services/api';
import toast from 'react-hot-toast';

const OnboardUser = () => {const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState(false);

  // Verify token and fetch user details on mount
  useEffect(() => {
    const verifyToken = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/auth/onboard/${token}`);
        setUser(response.data.user);
        setError(null);
      } catch (err) {
        console.error('Token verification failed:', err);
        setError(err.response?.data?.message || 'The invitation link is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      verifyToken();
    } else {
      setError('No invitation token provided.');
      setLoading(false);
    }
  }, [token]);

  const handleSetup = async (e) => {
    e.preventDefault();

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters long.');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }

    try {
      setVerifying(true);
      const response = await api.post('/auth/onboard/setup', {
        token,
        password
      });

      setSuccess(true);
      toast.success('Account setup successful!');

      // Wait 2 seconds and redirect to login or auto-login
      setTimeout(() => {
        // If the backend returns a token, we could auto-login
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          navigate('/admin/dashboard');
        } else {
          navigate('/login');
        }
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to setup password.');
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center p-4">
                <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                <p className="mt-6 text-zinc-400 font-medium animate-pulse">Verifying invitation...</p>
            </div>);

  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center p-4">
                <div className="max-w-md w-full bg-zinc-900 border border-zinc-800 rounded-3xl p-8 shadow-2xl text-center">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-500/10 text-red-500 mb-6">
                        <RiErrorWarningLine size={40} />
                    </div>
                    <h1 className="text-2xl font-bold text-white mb-2">{t("invalid_link")}</h1>
                    <p className="text-zinc-400 mb-8">{error}</p>
                    <button
            onClick={() => navigate('/login')}
            className="w-full bg-zinc-800 hover:bg-zinc-700 text-white font-semibold py-4 rounded-2xl transition-all duration-300 flex items-center justify-center space-x-2">
            
                        <span>{t("go_to_login")}</span>
                        <RiArrowRightLine />
                    </button>
                </div>
            </div>);

  }

  if (success) {
    return (
      <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center p-4">
                <div className="max-w-md w-full bg-zinc-900 border border-zinc-800 rounded-3xl p-8 shadow-2xl text-center">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/10 text-green-500 mb-6">
                        <RiCheckDoubleLine size={40} className="animate-bounce" />
                    </div>
                    <h1 className="text-2xl font-bold text-white mb-2">{t("welcome_aboard")}</h1>
                    <p className="text-zinc-400 mb-8">Your account has been secured and set up correctly. Redirecting you to your dashboard...</p>
                    <div className="flex justify-center space-x-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce delay-75"></div>
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce delay-150"></div>
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce delay-200"></div>
                    </div>
                </div>
            </div>);

  }

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
            <div className="max-w-4xl w-full grid grid-cols-1 md:grid-cols-2 bg-zinc-900 border border-zinc-800 rounded-3xl overflow-hidden shadow-2xl">
                {/* Left side: branding/welcome */}
                <div className="bg-gradient-to-br from-red-900/40 via-red-950/20 to-black p-10 flex flex-col justify-between border-r border-zinc-800">
                    <div>
                        <img src="https://quadcores.com/quadcores-logo1.png" alt="Quadcores" className="h-10 mb-12" />
                        <h1 className="text-3xl font-bold text-white mb-4">{t("secure_onboarding")}</h1>
                        <p className="text-zinc-400">{t("welcome_to_the_quadcores_property_manage")}</p>
                    </div>

                    <div className="space-y-6">
                        <div className="flex items-center space-x-4 p-4 bg-white/5 rounded-2xl border border-white/5 backdrop-blur-sm">
                            <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center text-red-500">
                                <RiUser3Line />
                            </div>
                            <div>
                                <p className="text-xs text-zinc-500 uppercase tracking-widest font-bold">{t("account_role")}</p>
                                <p className="text-white font-medium capitalize">{user.role.replace('_', ' ')}</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-4 p-4 bg-white/5 rounded-2xl border border-white/5 backdrop-blur-sm">
                            <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center text-red-500">
                                <RiMailLine />
                            </div>
                            <div>
                                <p className="text-xs text-zinc-500 uppercase tracking-widest font-bold">{t("email_address_1")}</p>
                                <p className="text-white font-medium">{user.email}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right side: password form */}
                <div className="p-10 flex flex-col justify-center">
                    <div className="mb-8">
                        <h2 className="text-xl font-bold text-white mb-2">{t("set_your_password")}</h2>
                        <p className="text-zinc-500 text-sm">{t("please_choose_a_secure_password_for_your")}</p>
                    </div>

                    <form onSubmit={handleSetup} className="space-y-5">
                        <div>
                            <label className="block text-sm font-medium text-zinc-400 mb-2">{t("new_password")}</label>
                            <div className="relative group">
                                <span className="absolute inset-y-0 left-0 pl-4 flex items-center text-zinc-500 group-focus-within:text-red-500 transition-colors">
                                    <RiLockPasswordLine size={20} />
                                </span>
                                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-12 pr-4 py-4 bg-zinc-800 border border-zinc-700 rounded-2xl text-white focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:border-red-500 transition-all duration-300"
                  placeholder="Enter secure password"
                  required />
                
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-zinc-400 mb-2">{t("confirm_password")}</label>
                            <div className="relative group">
                                <span className="absolute inset-y-0 left-0 pl-4 flex items-center text-zinc-500 group-focus-within:text-red-500 transition-colors">
                                    <RiShieldCheckLine size={20} />
                                </span>
                                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="block w-full pl-12 pr-4 py-4 bg-zinc-800 border border-zinc-700 rounded-2xl text-white focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:border-red-500 transition-all duration-300"
                  placeholder="Re-enter password"
                  required />
                
                            </div>
                        </div>

                        <div className="flex items-center space-x-2 py-2">
                            <input
                id="show-pass"
                type="checkbox"
                checked={showPassword}
                onChange={() => setShowPassword(!showPassword)}
                className="w-4 h-4 rounded bg-zinc-800 border-zinc-700 text-red-600 focus:ring-red-500" />
              
                            <label htmlFor="show-pass" className="text-sm text-zinc-500 cursor-pointer hover:text-zinc-300 transition-colors">{t("show_password")}</label>
                        </div>

                        <button
              type="submit"
              disabled={verifying}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-4 rounded-2xl transition-all duration-300 shadow-xl shadow-red-900/20 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed group">
              
                            {verifying ?
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div> :

              <>
                                    <span>{t("complete_account_setup")}</span>
                                    <RiArrowRightLine className="group-hover:translate-x-1 transition-transform" />
                                </>
              }
                        </button>
                    </form>

                    <div className="mt-8 pt-8 border-t border-zinc-800 flex items-start space-x-3">
                        <div className="p-2 bg-zinc-800 rounded-lg text-zinc-400">
                            <RiShieldCheckLine size={18} />
                        </div>
                        <p className="text-xs text-zinc-500 leading-relaxed">
                            {t("password_requirement_minimum_8_character")}
                        </p>
                    </div>
                </div>
            </div>
        </div>);

};

export default OnboardUser;